import React, { useCallback, useMemo } from "react";
import GenericTable from "./GenericTable";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import LogoutOutlined from "@mui/icons-material/LogoutOutlined";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import { Link } from "react-router-dom";
import { Avatar, IconButton, Tooltip, Box, Chip, Switch, Stack, Typography, useTheme } from "@mui/material";
import { formatDateTimePtBR } from "../utils/dateTime";
import { useTranslation } from "react-i18next";
import { useAuthContext } from "../context/AuthContext";
import {
  useEffectivePermissions,
  effectiveScopeSetForUser,
  can,
} from "../hooks/useEffectivePermissions";
import { canOfferForceLogoutOthers } from "../utils/forceLogoutUi";
import { useTeamSocketPresence } from "../hooks/useTeamSocketPresence";
import { getTeamPresenceKind } from "../utils/teamPresence";

const UserTable = ({
  users = [],
  rowCount,
  pageSize,
  page,
  onPageChange,
  onPageSizeChange,
  onSearch,
  searchValue,
  onSearchChange,
  onEditUser,
  onDeleteUser,
  onForceLogout,
  currentUserId,
  onDeleteSelected,
  onToggleStatus,
  isLoading,
  showOperatorAnalytics,
}) => {
  const { t } = useTranslation("settings");
  const theme = useTheme();
  const { userData } = useAuthContext();
  const { data: permsData } = useEffectivePermissions();
  const { tabsByUserId } = useTeamSocketPresence(userData);
  const isMasterUser = Number(userData?.master) === 1;
  const userScopeSet = useMemo(
    () => effectiveScopeSetForUser(permsData?.scopes, { isMaster: isMasterUser }),
    [permsData?.scopes, isMasterUser]
  );
  const canDeleteUsers = isMasterUser || can(userScopeSet, "users", "delete");
  const canUpdateUsers = isMasterUser || can(userScopeSet, "users", "update");
  const assignablePositionIds = permsData?.assignablePositionIds;
  const unrestrictedDeleteTargets =
    isMasterUser || !Array.isArray(assignablePositionIds) || assignablePositionIds.length === 0;
  const allowedDeleteCargoIds = useMemo(() => {
    if (unrestrictedDeleteTargets) return null;
    return new Set(assignablePositionIds.map(Number));
  }, [unrestrictedDeleteTargets, assignablePositionIds]);
  const canDeleteRowByCargo = useCallback(
    (row) => {
      if (unrestrictedDeleteTargets) return true;
      const cargoId = row?.id_cargo != null ? Number(row.id_cargo) : 0;
      return allowedDeleteCargoIds?.has(cargoId) === true;
    },
    [unrestrictedDeleteTargets, allowedDeleteCargoIds]
  );
  const selfId = currentUserId != null ? Number(currentUserId) : userData?.id != null ? Number(userData.id) : null;

  const rows = useMemo(
    () =>
      (users || []).map((u) => {
        const idNum = Number(u?.id);
        const socketTabs = Number.isFinite(idNum) ? tabsByUserId.get(idNum) || 0 : 0;
        const presenceKind = getTeamPresenceKind(
          { data_logout: u?.data_logout, data_ultimo_acesso: u?.data_ultimo_acesso },
          socketTabs
        );
        return {
          id: u?.id,
          nome: u?.nome || "",
          email: u?.email || "",
          cargo: u?.cargo_nome || "",
          id_cargo: u?.id_cargo != null ? Number(u.id_cargo) : null,
          ura: Number(u?.ura) === 1,
          statusRaw: Number(u?.status) === 1 ? 1 : 0,
          presenceKind,
          socketTabs,
          idInterno: u?.id_interno || "",
          createdAt: u?.data_cadastro || null,
          updatedAt: u?.data_ultima_alteracao || null,
          lastAccess: u?.data_ultimo_acesso || null,
          avatarUrl: u?.imagem || "",
        };
      }),
    [users, tabsByUserId]
  );

  const presenceTooltip = useCallback(
    (kind, tabs) => {
      const tip =
        kind === "online"
          ? t("dashboardPage.live.onlinePresenceTooltip")
          : kind === "away"
            ? t("dashboardPage.live.awayPresenceTooltip")
            : t("dashboardPage.live.offlinePresenceTooltip");
      if (kind === "online" && tabs > 0) {
        return `${tip}\n${t("dashboardPage.live.openTabsCount", { count: tabs })}`;
      }
      return tip;
    },
    [t]
  );

  const presenceDotBg = useCallback(
    (kind) => {
      if (kind === "online") return theme.palette.success.main;
      if (kind === "away") return theme.palette.warning.main;
      return theme.palette.error.main;
    },
    [theme]
  );

  const initials = (s) =>
    String(s || "—")
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase())
      .join("") || "—";
      
  const columns = useMemo(() => [
    {
      field: "nome",
      headerName: t("userTable.colUser"),
      width: 380,
      renderCell: (p) => (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ minWidth: 0, width: "100%" }}   // permite ellipsis no texto
        >
          {/* Coluna esquerda: avatar + presença */}
          <Box sx={{ position: "relative", flexShrink: 0 }}>
            <Avatar
              src={p?.row?.avatarUrl || undefined}
              sx={{ width: 24, height: 24, fontSize: 11, bgcolor: "grey.200", color: "text.primary" }}
            >
              {!p?.row?.avatarUrl && initials(p?.row?.nome)}
            </Avatar>
            <Tooltip
              title={presenceTooltip(p?.row?.presenceKind, p?.row?.socketTabs || 0)}
              placement="right"
              arrow
            >
              <Box
                component="span"
                sx={{
                  position: "absolute",
                  right: -1,
                  bottom: -1,
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: presenceDotBg(p?.row?.presenceKind),
                  border: "2px solid",
                  borderColor: "background.paper",
                  cursor: "help",
                }}
              />
            </Tooltip>
          </Box>

          {/* Coluna direita: nome em cima, e-mail embaixo */}
          <Box sx={{ minWidth: 0, overflow: "hidden" }}>
            <Typography
              fontWeight={600}
              noWrap
              title={p?.row?.nome ?? "—"}
            >
              {p?.row?.nome ?? "—"}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              title={p?.row?.email ?? "—"}
              sx={{ display: "block" }}
            >
              {p?.row?.email ?? "—"}
            </Typography>
          </Box>
        </Stack>
      ),
    },
    // {
    //   field: "nome",
    //   headerName: "Usuário",
    //   flex: 1,
    //   minWidth: 340,
    //   renderCell: (p) => (
    //     <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0, overflow: "hidden" }}>
    //       {/* Avatar + presença (sem forçar altura) */}
    //       <Box sx={{ position: "relative", flexShrink: 0 }}>
    //         <Avatar sx={{ width: 24, height: 24, fontSize: 11, bgcolor: "grey.200", color: "text.primary" }}>
    //           {initials(p?.row?.nome)}
    //         </Avatar>
    //         <Box
    //           sx={{
    //             position: "absolute", right: -1, bottom: -1,
    //             width: 8, height: 8, borderRadius: "50%",
    //             bgcolor: p?.row?.online ? "success.main" : "text.disabled",
    //             border: "2px solid", borderColor: "background.paper",
    //           }}
    //         />
    //       </Box>

    //       {/* Texto em uma única linha com ellipsis */}
    //       <Typography
    //         noWrap
    //         title={`${p?.row?.nome ?? "—"} • ${p?.row?.email ?? "—"}`}
    //         sx={{ minWidth: 0 }}
    //       >
    //         <Box component="span" sx={{ fontWeight: 600 }}>
    //           {p?.row?.nome ?? "—"}
    //         </Box>
    //         {p?.row?.email && (
    //           <Box component="span" sx={{ ml: 1, color: "text.secondary" }}>
    //             • {p.row.email}
    //           </Box>
    //         )}
    //       </Typography>
    //     </Stack>
    //   ),
    // },
    { field: "cargo", headerName: t("userTable.colRole"), width: 180 },
    {
      field: "ura",
      headerName: t("userTable.colType"),
      width: 100,
      renderCell: (p) =>
        p?.row?.ura ? (
          <Chip size="small" label="ChatBot" color="warning" variant="outlined" />
        ) : (
          <Typography variant="caption" color="text.secondary">{t("userTable.human")}</Typography>
        ),
    },
    // {
    //   field: "updatedAt",
    //   headerName: "Última alteração",
    //   width: 180,
    //   // usar renderCell evita acessos a params.value quando params é null
    //   renderCell: (p) => <span>{fmtDateTime(p?.row?.updatedAt)}</span>,
    // },
    {
      field: "lastAccess",
      headerName: t("userTable.colLastAccess"),
      width: 180,
      renderCell: (p) => <span>{formatDateTimePtBR(p?.row?.lastAccess)}</span>,
    },
    {
      field: "status",
      headerName: t("userTable.colStatus"),
      width: 160,
      renderCell: (p) => (
        <Stack direction="row" alignItems="center" spacing={1}>
          <Switch
            size="small"
            checked={Number(p?.row?.statusRaw) === 1}
            onChange={() => onToggleStatus?.(p?.row)}
          />
          <Chip
            size="small"
            label={Number(p?.row?.statusRaw) === 1 ? t("shared.active") : t("shared.inactive")}
            color={Number(p?.row?.statusRaw) === 1 ? "success" : "default"}
            variant="outlined"
          />
        </Stack>
      ),
    },
    {
      field: "actions",
      headerName: t("userTable.colActions"),
      width: onForceLogout ? (showOperatorAnalytics ? 248 : 200) : showOperatorAnalytics ? 188 : 140,
      sortable: false,
      renderCell: (p) => {
        const rid = p?.row?.id != null ? Number(p.row.id) : NaN;
        const isSelf = selfId != null && Number.isFinite(rid) && rid === selfId;
        const deleteAllowedByCargo = canDeleteRowByCargo(p?.row);
        const deleteAllowed = canDeleteUsers && !isSelf && deleteAllowedByCargo;
        const deleteTip = isSelf
          ? t("addUserForm.roleOwnLocked")
          : !deleteAllowedByCargo
            ? t("addUserForm.roleDeleteLocked")
            : t("shared.delete");
        const canForceLogout =
          typeof onForceLogout === "function" &&
          canOfferForceLogoutOthers({
            isMaster: isMasterUser,
            scopeSet: userScopeSet,
            selfId,
            targetId: rid,
          });
        return (
          <Box display="flex" gap={0.5} alignItems="center" flexWrap="nowrap">
            {showOperatorAnalytics ? (
              <Tooltip title={t("userTable.operatorAnalytics")}>
                <IconButton
                  size="small"
                  component={Link}
                  to={`/settings/users/${encodeURIComponent(String(p?.row?.id))}/analytics`}
                >
                  <BarChartRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
            {canUpdateUsers ? (
              <Tooltip title={t("shared.edit")}>
                <IconButton size="small" onClick={() => onEditUser?.(p?.row)}>
                  <EditRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
            {canDeleteUsers ? (
              <Tooltip title={deleteTip}>
                <span>
                  <IconButton
                    size="small"
                    disabled={!deleteAllowed}
                    onClick={() => deleteAllowed && onDeleteUser?.(p?.row?.id)}
                  >
                    <DeleteOutlineRoundedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
            {canForceLogout ? (
              <Tooltip title={t("dashboardPage.live.forceLogoutTooltip")}>
                <IconButton
                  size="small"
                  color="warning"
                  onClick={() => onForceLogout(p.row)}
                  aria-label={t("dashboardPage.live.forceLogoutAction")}
                >
                  <LogoutOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
          </Box>
        );
      },
    },
  ], [t, theme, presenceTooltip, presenceDotBg, onEditUser, onDeleteUser, onForceLogout, onToggleStatus, selfId, isMasterUser, userScopeSet, showOperatorAnalytics, canDeleteRowByCargo, canDeleteUsers, canUpdateUsers]);

  const handleDeleteSelected = useCallback(
    (ids) => {
      if (!canDeleteUsers || !onDeleteSelected || !Array.isArray(ids) || !ids.length) return;
      const allowedIds = ids.filter((id) => {
        const row = rows.find((r) => Number(r.id) === Number(id));
        if (!row) return false;
        if (selfId != null && Number(row.id) === selfId) return false;
        return canDeleteRowByCargo(row);
      });
      if (!allowedIds.length) return;
      return onDeleteSelected(allowedIds);
    },
    [canDeleteUsers, onDeleteSelected, rows, selfId, canDeleteRowByCargo]
  );

  return (
    <GenericTable
      rows={rows}
      columns={columns}
      rowCount={rowCount}
      pageSize={pageSize}
      page={page}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      onSearch={onSearch}
      searchValue={searchValue}
      onSearchChange={onSearchChange}
      isLoading={isLoading}
      onExportPDFTitle={t("userTable.exportTitle")}
      exportFileName="usuarios"
      onDeleteSelected={canDeleteUsers && onDeleteSelected ? handleDeleteSelected : undefined}
      searchPlaceholder={t("userTable.searchPlaceholder")}
      pageSizeOptions={[10, 25, 50, 100]}
      dense
    />
  );
};

export default UserTable;
